import { Link } from 'react-router-dom';
import { ArrowRight, Check, CircleHelp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PageHero } from '../components/PageHero';
import { WebsitePackage } from '../components/WebsitePackage';
import { SeoHead } from '../components/SeoHead';

const packages = [
  {
    name: 'Custom Website',
    fit: 'For a business that needs more pages, a larger content structure, or features beyond the $995 package.',
    items: ['Custom website strategy and build', 'Mobile conversion paths', 'Forms and call routing', 'Analytics foundations', 'Launch and managed hosting scope'],
    color: 'bg-verto-fog',
    stamp: 'Custom quote',
  },
  {
    name: 'Website + Lead Capture',
    fit: 'For a business that needs the website and CRM handoff to behave like one system.',
    items: ['Everything in Custom Website', 'CRM pipeline and lead ownership', 'Immediate response workflows', 'Booking or next-step automation', 'Reminder and follow-up rules'],
    color: 'bg-verto-mint',
    stamp: 'Custom quote',
  },
  {
    name: 'Acquisition System',
    fit: 'For a business ready to add paid or organic demand to a working conversion and response path.',
    items: ['Everything in Website + Lead Capture', 'Google Search or selected Meta scope', 'Campaign landing pages', 'Call and form attribution', 'Lead-quality review and reporting'],
    color: 'bg-verto-white',
    stamp: 'Custom quote',
  },
];

const faqs = [
  ['What if I need more than the $995 package?', 'The five-page package has a fixed build price. Larger sites, CRM workflows, ads, and continuing support need their own written scope. We discuss those costs before you commit.'],
  ['What appears separately on a proposal?', 'Implementation, monthly management, platform or usage charges, and ad spend are shown as separate lines. Optional work stays optional.'],
  ['Can I start with only a website?', 'Yes. Websites remain the core offer. CRM workflows, AI reception, local SEO, and paid ads are added only when the business has a clear use for them.'],
  ['Do I own my accounts and data?', 'The proposal identifies domain, advertising accounts, phone numbers, CRM data, creative, content, code or site license, and offboarding responsibilities. Read those terms before signing.'],
];

export function Pricing() {
  return (
    <div>
      <SeoHead title="Five-Page Websites for $995 | Verno Web" description="Five-page website builds are $995 with basic hosting included. Domain costs are separate. Start with a free homepage demo; larger projects are quoted individually." path="/pricing" />
      <PageHero title="A complete business website. A clear $995 build price." subtitle="Up to five pages, basic hosting included, and a free homepage demo after a short call. Domain registration and renewal are separate. Larger builds and extra services are quoted individually." align="left">
        <Button asChild size="lg"><Link to="/free-demo">Request a free homepage demo <ArrowRight className="ml-2 h-5 w-5" /></Link></Button>
      </PageHero>
      <WebsitePackage />

      <section className="py-20 md:py-28">
        <div className="page-wrap">
          <h2 className="section-title">Need a larger build or ongoing support?</h2>
          <p className="body-copy mb-10 mt-5">These projects are quoted individually. Their services are separate from the five-page $995 package.</p>
          <div className="grid gap-6 lg:grid-cols-3">
            {packages.map((plan, index) => (
              <div key={plan.name} className={`paper-record ${plan.color} p-7 pl-11 ${index === 1 ? 'lg:-translate-y-4' : ''}`}>
                <span className="stamp">{plan.stamp}</span>
                <h2 className="mt-7 font-display text-4xl font-bold leading-none">{plan.name}</h2>
                <p className="mt-4 min-h-24 text-sm leading-6 text-verto-black/60">{plan.fit}</p>
                <ul className="mt-7 space-y-3 border-t border-verto-black/15 pt-6">
                  {plan.items.map((item) => <li key={item} className="flex gap-2.5 text-sm leading-6"><Check className="mt-1 h-4 w-4 shrink-0" />{item}</li>)}
                </ul>
                <Button asChild variant="outline" className="mt-8 w-full"><Link to="/free-demo">Talk with Nate about a custom scope</Link></Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-verto-black bg-verto-black py-20 text-verto-white md:py-24">
        <div className="page-wrap grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
          <div><h2 className="section-title">The four lines that make scope legible.</h2><p className="mt-5 text-sm leading-6 text-verto-white/60">Your proposal should make it easy to see what changes once, what continues, and what varies with use.</p></div>
          <div className="divide-y divide-white/15 border-y border-white/15">
            {[
              ['Implementation', 'Strategy, copy, design, development, integration, configuration, testing, and launch work.'],
              ['Monthly management', 'The ongoing work Verno is responsible for operating, reviewing, or updating.'],
              ['Platform and usage', 'CRM, phone, AI minutes, messages, email, calendar, or third-party subscriptions.'],
              ['Media spend', 'Budget paid to Google, Meta, or another advertising platform, separate from management.'],
            ].map(([title, copy]) => <div key={title} className="grid gap-3 py-6 sm:grid-cols-[12rem_1fr]"><h3 className="font-display text-2xl font-bold text-verto-mint">{title}</h3><p className="text-sm leading-6 text-verto-white/60">{copy}</p></div>)}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="page-wrap grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
          <div><h2 className="section-title">Questions before money changes hands.</h2><p className="body-copy mt-5">If the scope cannot answer these, it is not ready.</p></div>
          <div className="divide-y divide-verto-black/15 border-y border-verto-black/15">
            {faqs.map(([q, a]) => <div key={q} className="grid gap-3 py-6 sm:grid-cols-[2rem_1fr]"><CircleHelp className="mt-1 h-5 w-5 text-verto-deep" /><div><h3 className="font-display text-2xl font-bold">{q}</h3><p className="mt-3 text-sm leading-6 text-verto-black/60">{a}</p></div></div>)}
          </div>
        </div>
      </section>

      <section className="bg-verto-mint py-16 md:py-20"><div className="page-wrap flex flex-col justify-between gap-8 lg:flex-row lg:items-end"><div><h2 className="section-title">Get the scope before you compare proposals.</h2><p className="mt-5 max-w-2xl text-base leading-7 text-verto-black/65">We’ll talk about your business, confirm whether the package fits, and agree a written scope before payment.</p></div><Button asChild size="lg"><Link to="/free-demo">Request a free homepage demo <ArrowRight className="ml-2 h-5 w-5" /></Link></Button></div></section>
    </div>
  );
}
